import { WORKSPACE_AGENT_PANEL_ROUNDED_CORNERS_PREVIEW } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import {
  getWorkspaceAgentGuidedVariantCanvasColor,
  type WorkspaceAgentGuidedVariantColorOptionId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantColor';
import {
  buildWorkspaceAgentGuidedVariantSkipNonImagesClause,
  getWorkspaceAgentGuidedVariantImageOutputPhrases,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantImagePromptPhrases';
import {
  hasWorkspaceAgentGuidedVariantChosenImageFormat,
  resolveWorkspaceAgentGuidedVariantPromptRenameSuffix,
  WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  type WorkspaceAgentGuidedVariantOutput,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantOutput';
import {
  type WorkspaceAgentGuidedVariantSaveDestination,
  withWorkspaceAgentGuidedVariantSaveDestinationAndRename,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentGuidedVariantSaveLocation';

export const WORKSPACE_AGENT_ROUNDED_CORNERS_DEFAULT_RADIUS = 6;
export const WORKSPACE_AGENT_ROUNDED_CORNERS_MAX_PERCENT_RADIUS = 50;
export const WORKSPACE_AGENT_ROUNDED_CORNERS_MAX_PX_RADIUS = 1024;

export type WorkspaceAgentRoundedCornersUnitId = 'percent' | 'px';

export const getWorkspaceAgentRoundedCornersMaxRadius = (unit: WorkspaceAgentRoundedCornersUnitId): number =>
  unit === 'percent'
    ? WORKSPACE_AGENT_ROUNDED_CORNERS_MAX_PERCENT_RADIUS
    : WORKSPACE_AGENT_ROUNDED_CORNERS_MAX_PX_RADIUS;

export type WorkspaceAgentRoundedCornersPreviewRadiusInput = {
  height: number | null;
  radius: number;
  unit: WorkspaceAgentRoundedCornersUnitId;
  width: number | null;
};

/** CSS `border-radius` for the 80px preview. Percent applies as CSS percent; px is scaled to the well. */
export const getWorkspaceAgentRoundedCornersPreviewBorderRadius = ({
  height,
  radius,
  unit,
  width,
}: WorkspaceAgentRoundedCornersPreviewRadiusInput): string => {
  if (unit === 'percent') {
    return `${radius}%`;
  }

  if (!width || !height) {
    return `${radius}px`;
  }

  const shortSide = Math.min(width, height);

  return `${(radius * WORKSPACE_AGENT_PANEL_ROUNDED_CORNERS_PREVIEW.width) / shortSide}px`;
};

export const getWorkspaceAgentRoundedCornersRadiusLabel = (
  radius: number,
  unit: WorkspaceAgentRoundedCornersUnitId,
): string => `${radius}${unit === 'percent' ? '%' : 'px'}`;

export type BuildWorkspaceAgentRoundedCornersPromptInput = {
  customColor: string;
  destination: WorkspaceAgentGuidedVariantSaveDestination;
  imageCount: number;
  output?: WorkspaceAgentGuidedVariantOutput;
  radius: number;
  skippedFileCount: number;
  skippedVideoCount: number;
  unit: WorkspaceAgentRoundedCornersUnitId;
  cornerColorId: WorkspaceAgentGuidedVariantColorOptionId;
};

export const buildWorkspaceAgentRoundedCornersPrompt = ({
  cornerColorId,
  customColor,
  destination,
  imageCount,
  output = WORKSPACE_AGENT_GUIDED_VARIANT_DEFAULT_OUTPUT,
  radius,
  skippedFileCount,
  skippedVideoCount,
  unit,
}: BuildWorkspaceAgentRoundedCornersPromptInput): string => {
  const color = getWorkspaceAgentGuidedVariantCanvasColor(cornerColorId, customColor);
  const { subject } = getWorkspaceAgentGuidedVariantImageOutputPhrases(imageCount);
  const radiusLabel = getWorkspaceAgentRoundedCornersRadiusLabel(radius, unit);
  const percentNote = unit === 'percent' ? ' The percentage is relative to the shorter side of the image.' : '';
  let suffix = `${cornerColorId}-rounded`;

  if (cornerColorId === 'transparent') {
    suffix = 'rounded';
  } else if (cornerColorId === 'custom') {
    suffix = 'custom-rounded';
  }

  const keepOriginalFileType = !hasWorkspaceAgentGuidedVariantChosenImageFormat(output);
  let cornerInstruction = color
    ? `Fill the corner regions outside the rounded mask with solid ${color}.`
    : 'Keep the corner regions transparent and do not fill them with a color.';

  if (color && keepOriginalFileType) {
    cornerInstruction += ' Keep the original file type.';
  } else if (!color && keepOriginalFileType) {
    cornerInstruction += ' Save as PNG with an alpha channel so the corners stay transparent.';
  }
  const skipNonImages = buildWorkspaceAgentGuidedVariantSkipNonImagesClause({
    featureAppliesToImagesPhrase: 'rounded corners only apply to images',
    nonImageFilesWording: 'non-image files',
    skippedFileCount,
    skippedVideoCount,
  });

  return withWorkspaceAgentGuidedVariantSaveDestinationAndRename(
    `Round the corners of ${subject}. Apply a ${radiusLabel} corner radius to all four corners.${percentNote} ${cornerInstruction} Do not recreate or reimagine the scene — keep the same subject, pose, composition, framing, and fine detail.${skipNonImages}`,
    destination,
    resolveWorkspaceAgentGuidedVariantPromptRenameSuffix(suffix, output),
  );
};
